/**
 * Microsoft Clarity user identification utility
 *
 * This utility provides functions to identify users in Microsoft Clarity
 * for better session tracking and user journey analysis.
 */

/**
 * Identify a user in Microsoft Clarity
 * @param {string} userId - Unique user identifier (email, username, or user_id)
 * @param {Object} customTags - Optional custom tags to associate with the user
 * @param {string} customTags.email - User's email address
 * @param {string} customTags.username - User's username
 * @param {string} customTags.usertype - User's role/type
 * @param {string} customTags.user_id - Internal user ID
 */
export const identifyUser = (userId, customTags = {}) => {
    // Check if Clarity is available
    if (typeof window !== 'undefined' && window.clarity) {
        try {
            // Identify the user with their primary identifier
            window.clarity('identify', userId, customTags);
        } catch (error) {
            console.error('Error identifying user in Clarity:', error);
        }
    } else {
        console.warn('Microsoft Clarity not available - user identification skipped');
    }
};

/**
 * Set custom tags for the current user session
 * @param {Object} tags - Key-value pairs of custom tags
 */
export const setCustomTags = (tags = {}) => {
    if (typeof window !== 'undefined' && window.clarity) {
        try {
            Object.entries(tags).forEach(([key, value]) => {
                window.clarity('set', key, value);
            });
        } catch (error) {
            console.error('Error setting custom tags in Clarity:', error);
        }
    } else {
        console.warn('Microsoft Clarity not available - custom tags skipped');
    }
};

/**
 * Clear user identification (useful for logout)
 */
export const clearUserIdentification = () => {
    if (typeof window !== 'undefined' && window.clarity) {
        try {
            // Clear user identification by calling identify with null
            window.clarity('identify', null);
        } catch (error) {
            console.error('Error clearing user identification in Clarity:', error);
        }
    }
};

/**
 * Track a custom event in Clarity
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Additional data for the event
 */
export const trackEvent = (eventName, eventData = {}) => {
    if (typeof window !== 'undefined' && window.clarity) {
        try {
            window.clarity('event', eventName, eventData);
        } catch (error) {
            console.error('Error tracking event in Clarity:', error);
        }
    }
};

/**
 * Send a beacon event to Microsoft Clarity (simplified event tracking)
 * @param {string} eventName - Name of the event to track
 * @param {Object} eventData - Optional additional data for the event
 */
export const beaconEvent = (eventName, eventData = {}) => {
    if (typeof window !== 'undefined' && window.clarity) {
        try {
            // If eventData is provided and not empty, use trackEvent-like behavior
            if (eventData && Object.keys(eventData).length > 0) {
                window.clarity('event', eventName, eventData);
            } else {
                window.clarity('event', eventName);
            }
        } catch (error) {
            console.error('Error sending beacon event to Clarity:', error);
        }
    }
}; 